import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

function Privacypolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const navigate = useNavigate();

  return (
    <div className="my-16 flex justify-center">
      <div className="w-[90%] pt-2">
        <h1 className="text-textcolor text-3xl lg:text-[40px] PoppinsRegular font-bold leading-[48px]">
          Privacy Policy
        </h1>

        <p className="text-textcolor/60 text-[15px] pt-3">
          <strong>Last Updated:</strong> April 28, 2026
        </p>

        <p className="text-textcolor/60 text-[15px] pt-3">
          This Privacy Policy explains how <strong>Team Edelstein Systems</strong> ("TES," "we," "us," or "our") collects, uses, shares, and protects information when you use the Platform.
        </p>

        <p className="text-textcolor/60 text-[15px] pt-3">
          This Policy applies to authorized users of TES and to information about clients and contacts processed through TES.
        </p>

        <h2 className="text-textcolor text-lg pt-7">
          If you do not agree with this Policy, do not use the Platform.
        </h2>

        {/* SECTION 1 */}
        <h2 className="text-textcolor text-xl pt-7 font-semibold">
          1. Information We Collect
        </h2>

        <h3 className="pt-5 font-semibold">1.1 Account and Profile Information</h3>
        <p className="text-textcolor/60 text-[15px] pt-2">
          We collect your name, email, profile photo, role, team affiliation, and authentication identifiers.
        </p>

        <h3 className="pt-5 font-semibold">1.2 Real Estate Transaction Data</h3>
        <ul className="list-disc ml-6 text-textcolor/60 text-[15px] pt-2 space-y-2">
          <li>Deal information (addresses, prices, MLS data, dates)</li>
          <li>Buyer/seller details and contacts</li>
          <li>Contracts and PDFs</li>
          <li>Commission structures and splits</li>
          <li>Notes, tasks, and pipeline stages</li>
          <li>Closing records</li>
        </ul>

        <h3 className="pt-5 font-semibold">1.3 Contacts and CRM Data</h3>
        <p className="text-textcolor/60 text-[15px] pt-2">
          We collect contact details and interaction history if you connect or upload contacts.
        </p>

        <h3 className="pt-5 font-semibold">1.4 Documents</h3>
        <p className="text-textcolor/60 text-[15px] pt-2">
          Uploaded documents and extracted AI fields are stored in TES.
        </p>

        <h3 className="pt-5 font-semibold">1.5 Activity and Usage Data</h3>
        <p className="text-textcolor/60 text-[15px] pt-2">
          Includes deal updates, logins, audit logs, and system activity.
        </p>

        <h3 className="pt-5 font-semibold">1.6 Technical Data</h3>
        <p className="text-textcolor/60 text-[15px] pt-2">
          Includes IP address, browser info, and device identifiers.
        </p>

        {/* SECTION 2 */}
        <h2 className="text-textcolor text-xl pt-7 font-semibold">
          2. How We Use Information
        </h2>
        <ul className="list-disc ml-6 text-textcolor/60 text-[15px] pt-2 space-y-2">
          <li>Operate and maintain the platform</li>
          <li>Authenticate users and enforce roles</li>
          <li>Track deals and commissions</li>
          <li>Generate reports and analytics</li>
          <li>AI extraction from contracts</li>
          <li>Communications and updates</li>
          <li>Legal compliance</li>
          <li>Fraud prevention</li>
        </ul>

        <p className="text-textcolor/60 text-[15px] pt-3">
          We do <strong>not</strong> sell personal data or use it for advertising.
        </p>

        {/* SECTION 3 */}
        <h2 className="text-textcolor text-xl pt-7 font-semibold">
          3. How We Share Information
        </h2>

        <h3 className="pt-5 font-semibold">3.1 Within Team Edelstein</h3>
        <p className="text-textcolor/60 text-[15px] pt-2">
          Access is role-based across team members.
        </p>

        <h3 className="pt-5 font-semibold">3.2 With Coldwell Banker</h3>
        <p className="text-textcolor/60 text-[15px] pt-2">
          Shared for compliance and brokerage requirements.
        </p>

        <h3 className="pt-5 font-semibold">3.3 Service Providers</h3>
        <ul className="list-disc ml-6 text-textcolor/60 text-[15px] pt-2 space-y-2">
          <li>Google / Firebase (auth, DB, hosting)</li>
          <li>Anthropic (AI document parsing)</li>
          <li>Other infrastructure providers</li>
        </ul>

        <h3 className="pt-5 font-semibold">3.4 Legal Requirements</h3>
        <p className="text-textcolor/60 text-[15px] pt-2">
          We may disclose data when required by law.
        </p>

        <h3 className="pt-5 font-semibold">3.5 Business Transfers</h3>
        <p className="text-textcolor/60 text-[15px] pt-2">
          Data may transfer during mergers or restructuring.
        </p>

        {/* SECTION 4 */}
        <h2 className="text-textcolor text-xl pt-7 font-semibold">
          4. Third-Party Integrations
        </h2>
        <p className="text-textcolor/60 text-[15px] pt-2">
          Access depends on permissions you grant (e.g., Google integrations).
        </p>

        {/* SECTION 5 */}
        <h2 className="text-textcolor text-xl pt-7 font-semibold">
          5. Data Storage and Security
        </h2>
        <ul className="list-disc ml-6 text-textcolor/60 text-[15px] pt-2 space-y-2">
          <li>HTTPS/TLS encryption</li>
          <li>Firebase encryption at rest</li>
          <li>OAuth authentication</li>
          <li>Role-based access</li>
          <li>Audit logging</li>
        </ul>

        {/* SECTION 6 */}
        <h2 className="text-textcolor text-xl pt-7 font-semibold">
          6. Data Retention
        </h2>
        <p className="text-textcolor/60 text-[15px] pt-2">
          Data is retained as needed for operations and legal obligations.
        </p>

        {/* SECTION 7 */}
        <h2 className="text-textcolor text-xl pt-7 font-semibold">
          7. Your Privacy Rights
        </h2>
        <ul className="list-disc ml-6 text-textcolor/60 text-[15px] pt-2 space-y-2">
          <li>Access your data</li>
          <li>Request corrections</li>
          <li>Request deletion</li>
          <li>Object to processing</li>
          <li>Data portability</li>
          <li>Withdraw consent</li>
        </ul>

        {/* SECTION 8 */}
        <h2 className="text-textcolor text-xl pt-7 font-semibold">
          8. Information About Clients
        </h2>
        <p className="text-textcolor/60 text-[15px] pt-2">
          Clients should contact their agent for data requests.
        </p>

        {/* SECTION 9 */}
        <h2 className="text-textcolor text-xl pt-7 font-semibold">
          9. Cookies
        </h2>
        <p className="text-textcolor/60 text-[15px] pt-2">
          Used for authentication and functionality only.
        </p>

        {/* SECTION 10 */}
        <h2 className="text-textcolor text-xl pt-7 font-semibold">
          10. Children's Privacy
        </h2>
        <p className="text-textcolor/60 text-[15px] pt-2">
          Not intended for users under 18.
        </p>

        {/* SECTION 11 */}
        <h2 className="text-textcolor text-xl pt-7 font-semibold">
          11. International Users
        </h2>
        <p className="text-textcolor/60 text-[15px] pt-2">
          Data is processed in the United States.
        </p>

        {/* SECTION 12 */}
        <h2 className="text-textcolor text-xl pt-7 font-semibold">
          12. AI and Automated Processing
        </h2>
        <p className="text-textcolor/60 text-[15px] pt-2">
          AI assists with data extraction but requires human verification.
        </p>

        {/* SECTION 13 */}
        <h2 className="text-textcolor text-xl pt-7 font-semibold">
          13. Changes to This Policy
        </h2>
        <p className="text-textcolor/60 text-[15px] pt-2">
          Updates will be communicated via the Platform.
        </p>

        {/* SECTION 14 */}
        <h2 className="text-textcolor text-xl pt-7 font-semibold">
          14. Contact Us
        </h2>

        <p className="text-textcolor/60 text-[15px] pt-3">
          Team Edelstein
        </p>
        <p className="text-textcolor/60 text-[15px]">
          Attn: Henry Edelstein, Privacy Contact
        </p>
        <p className="text-themeblue text-[15px] underline pt-2">
          henry@teamedelstein.com
        </p>
     
      </div>
    </div>
  );
}

export default Privacypolicy;